<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import AppButton from '@/common/AppButton.vue';
import FormPaymentButtons from '@/components/FormPaymentButtons.vue';
import FormLayout from '@/layouts/FormLayout.vue';
import { getCurrencySymbol, widgetViews } from '@/utils';
import { getUpsellDecision } from '@/services/upsell';

export default {
  name: 'Upsell',
  components: {
    AppButton,
    FormPaymentButtons,
    FormLayout
  },
  data() {
    return {
      widgetViews,
      getCurrencySymbol,
      paddingBottom: 140,
      selectedChoice: null
    };
  },
  computed: {
    ...mapState(['campaign']),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('publicOrgSettings', {
      publicSettings: (state) => state.settings
    }),
    ...mapGetters('campaign', ['shouldSkipAdditionalDetails']),
    settings() {
      return this.campaign.settings || {};
    },
    decision() {
      return getUpsellDecision({
        settings: this.settings,
        orgUpsellSettings: this.publicSettings.upsell,
        donation: this.donation,
        campaignId: this.campaign.id || this.donation.campaignId,
        campaignCurrency: this.campaign.currency
      });
    },
    currentDecision() {
      if (this.decision) {
        return this.decision;
      }

      return this.donation.upsell?.offeredAmounts?.length ? this.donation.upsell : null;
    },
    title() {
      return this.settings.upsellTitle || 'Make your impact monthly!';
    },
    description() {
      return this.settings.upsellDescription || 'Turn your one-time gift into lasting impact with a smaller monthly donation.';
    },
    keepDonationLabel() {
      return `Keep my ${this.amountLabel(this.currentDecision?.originalAmount || this.donation.amount)} one-time donation`;
    }
  },
  methods: {
    ...mapMutations(['SET_WIDGET_VIEW', 'SET_SHOW_PAYMENT_BUTTONS']),
    ...mapMutations('donation', ['SET_AMOUNT', 'SET_UPSELL']),
    ...mapActions('donation', ['SET_FREQUENCY_ACTIONS']),
    amountLabel(amount) {
      const value = Number(amount || 0);
      const formattedAmount = value % 1 === 0 ? value.toLocaleString('en') : value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${getCurrencySymbol(this.donation.currency)}${formattedAmount}`;
    },
    amountDescription(index) {
      return this.currentDecision?.offeredAmountDescriptions?.[index] || '';
    },
    isUpsellAmountSelected(amount, index) {
      if (!this.selectedChoice) {
        return index === 0;
      }
      return this.selectedChoice.type === 'upsell' && this.selectedChoice.amount === amount;
    },
    isOneTimeSelected() {
      return this.selectedChoice?.type === 'one-time';
    },
    selectUpsellOption(amount) {
      this.selectedChoice = { type: 'upsell', amount };
    },
    selectOneTimeOption() {
      this.selectedChoice = { type: 'one-time' };
    },
    continueCheckout() {
      if (this.shouldSkipAdditionalDetails) {
        this.SET_SHOW_PAYMENT_BUTTONS(true);
        return;
      }

      this.SET_WIDGET_VIEW(widgetViews.ADDITIONAL_DETAILS);
    },
    keepOneTimeDonation() {
      const decision = this.currentDecision;
      if (!decision) {
        return;
      }

      this.SET_UPSELL({
        ...decision,
        accepted: false,
        selectedFrequency: decision.originalFrequency,
        selectedAmount: decision.originalAmount
      });
      this.continueCheckout();
    },
    selectUpsellAmount(amount) {
      const decision = this.currentDecision;
      if (!decision) {
        return;
      }

      this.SET_FREQUENCY_ACTIONS('monthly');
      this.SET_AMOUNT(amount);
      this.SET_UPSELL({
        ...decision,
        accepted: true,
        selectedAmount: amount
      });
      this.continueCheckout();
    },
    continueWithSelectedOption() {
      if (!this.currentDecision) {
        return;
      }

      if (this.isOneTimeSelected()) {
        this.keepOneTimeDonation();
        return;
      }

      this.selectUpsellAmount(this.selectedChoice?.amount || this.currentDecision.offeredAmounts[0]);
    }
  },
  async mounted() {
    if (!this.currentDecision) {
      this.continueCheckout();
      return;
    }

    await this.$nextTick();
    this.paddingBottom = this.$refs.footerSticky.$el.offsetHeight;
  }
};
</script>

<template>
  <form-layout title="Become a monthly supporter" :back-view="widgetViews.DONATION_FORM" :style="`padding-bottom: ${paddingBottom}px`" :show-logo="false" class="upsell-view">
    <div v-if="currentDecision" class="upsell">
      <svg class="upsell-illustration" viewBox="0 0 82 80" aria-hidden="true" focusable="false">
        <path
          d="M8.82251 25.7109H81.9997V77.7262C81.9997 78.9809 80.9803 79.9994 79.7245 79.9994H11.0977C9.84195 79.9994 8.82251 78.9809 8.82251 77.7262V25.7109Z"
          fill="#091B27"
          fill-opacity="0.05"
        />
        <path
          d="M81.9998 25.7109C81.9998 49.0057 75.6606 64.3657 72.9374 69.8893C72.2478 71.2881 70.8235 72.1747 69.2626 72.1747H2.27834C0.612977 72.1747 -0.489513 70.445 0.217638 68.9386C3.13167 62.7318 8.82304 47.7828 8.82304 25.7109H82.0003H81.9998Z"
          fill="#DFE3E4"
        />
        <path
          d="M11.0977 6.1875H79.725C80.9808 6.1875 82.0002 7.206 82.0002 8.46064V25.7122H8.82251V8.46064C8.82251 7.206 9.84195 6.1875 11.0977 6.1875Z"
          fill="var(--primary-color)"
        />
        <path d="M15.4749 25.7109L12.149 29.0344L8.82251 25.7109H15.4749Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M22.128 25.7109L18.8016 29.0344L15.4751 25.7109H22.128Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M28.7804 25.7109L25.4539 29.0344L22.1279 25.7109H28.7804Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M35.4327 25.7109L32.1063 29.0344L28.7803 25.7109H35.4327Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M42.0853 25.7109L38.7588 29.0344L35.4329 25.7109H42.0853Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M48.7376 25.7109L45.4112 29.0344L42.0852 25.7109H48.7376Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M55.39 25.7109L52.0635 29.0344L48.7375 25.7109H55.39Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M62.0426 25.7109L58.7166 29.0344L55.3901 25.7109H62.0426Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M68.6949 25.7109L65.3689 29.0344L62.0425 25.7109H68.6949Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M75.3475 25.7109L72.0215 29.0344L68.6951 25.7109H75.3475Z" fill="#091B27" fill-opacity="0.05" />
        <path d="M81.9998 25.7109L78.6739 29.0344L75.3474 25.7109H81.9998Z" fill="#091B27" fill-opacity="0.05" />
        <path
          d="M26.4516 6.459C26.4053 4.70161 25.6686 3.03456 24.4031 1.82392C23.1377 0.613285 21.447 -0.0419588 19.7023 0.00208505C18.423 -0.012521 17.1658 0.338206 16.0761 1.01366C14.9865 1.68911 14.1091 2.66167 13.5455 3.81869C13.5175 3.87798 13.4734 3.92803 13.4183 3.96309C13.3632 3.99815 13.2994 4.01677 13.2342 4.01677C13.169 4.01677 13.1051 3.99815 13.05 3.96309C12.9949 3.92803 12.9508 3.87798 12.9228 3.81869C12.3592 2.66167 11.4818 1.68911 10.3922 1.01366C9.30255 0.338206 8.04528 -0.012521 6.76596 0.00208505C5.02133 -0.0419588 3.33064 0.613285 2.06519 1.82392C0.799754 3.03456 0.0630306 4.70161 0.0167568 6.459C-0.496246 16.767 10.9252 22.5284 12.9337 23.4608C13.0276 23.5047 13.1298 23.5274 13.2334 23.5274C13.3369 23.5274 13.4392 23.5047 13.5331 23.4608C15.5431 22.53 26.9646 16.767 26.4516 6.459Z"
          fill="#EA246B"
          transform="translate(28 37)"
        />
        <path
          d="M21.166 17.1499C23.3916 17.1499 25.1958 15.3474 25.1958 13.1238C25.1958 10.9002 23.3916 9.09766 21.166 9.09766C18.9404 9.09766 17.1362 10.9002 17.1362 13.1238C17.1362 15.3474 18.9404 17.1499 21.166 17.1499Z"
          fill="white"
        />
        <path
          d="M23.5826 2.41444C23.5826 1.08098 22.5007 0 21.1662 0C19.8316 0 18.7498 1.08098 18.7498 2.41444V12.4112C18.7498 13.7447 19.8316 14.8257 21.1662 14.8257C22.5007 14.8257 23.5826 13.7447 23.5826 12.4112V2.41444Z"
          fill="#5F7D8A"
        />
        <path
          d="M37.3299 17.1499C39.5555 17.1499 41.3597 15.3474 41.3597 13.1238C41.3597 10.9002 39.5555 9.09766 37.3299 9.09766C35.1043 9.09766 33.3 10.9002 33.3 13.1238C33.3 15.3474 35.1043 17.1499 37.3299 17.1499Z"
          fill="white"
        />
        <path
          d="M39.7459 2.41444C39.7459 1.08098 38.664 0 37.3295 0C35.9949 0 34.9131 1.08098 34.9131 2.41444V12.4112C34.9131 13.7447 35.9949 14.8257 37.3295 14.8257C38.664 14.8257 39.7459 13.7447 39.7459 12.4112V2.41444Z"
          fill="#5F7D8A"
        />
        <path
          d="M53.4932 17.1499C55.7188 17.1499 57.523 15.3474 57.523 13.1238C57.523 10.9002 55.7188 9.09766 53.4932 9.09766C51.2676 9.09766 49.4634 10.9002 49.4634 13.1238C49.4634 15.3474 51.2676 17.1499 53.4932 17.1499Z"
          fill="white"
        />
        <path
          d="M55.9092 2.41444C55.9092 1.08098 54.8274 0 53.4928 0C52.1583 0 51.0764 1.08098 51.0764 2.41444V12.4112C51.0764 13.7447 52.1583 14.8257 53.4928 14.8257C54.8274 14.8257 55.9092 13.7447 55.9092 12.4112V2.41444Z"
          fill="#5F7D8A"
        />
        <path
          d="M69.6565 17.1499C71.8821 17.1499 73.6863 15.3474 73.6863 13.1238C73.6863 10.9002 71.8821 9.09766 69.6565 9.09766C67.4309 9.09766 65.6267 10.9002 65.6267 13.1238C65.6267 15.3474 67.4309 17.1499 69.6565 17.1499Z"
          fill="white"
        />
        <path
          d="M72.0731 2.41444C72.0731 1.08098 70.9912 0 69.6566 0C68.3221 0 67.2402 1.08098 67.2402 2.41444V12.4112C67.2402 13.7447 68.3221 14.8257 69.6566 14.8257C70.9912 14.8257 72.0731 13.7447 72.0731 12.4112V2.41444Z"
          fill="#5F7D8A"
        />
      </svg>

      <div class="upsell-copy">
        <h3 class="bold">{{ title }}</h3>
        <p>{{ description }}</p>
      </div>

      <div class="upsell-options">
        <app-button
          v-for="(amount, index) in currentDecision.offeredAmounts"
          :key="amount"
          variant="outline"
          :selected="isUpsellAmountSelected(amount, index)"
          class="upsell-option"
          @click="selectUpsellOption(amount)"
        >
          <span class="upsell-option-content">
            <span class="upsell-amount">
              <span>{{ amountLabel(amount) }}</span>
              <span class="frequency">/mo</span>
            </span>
            <span v-if="amountDescription(index)" class="upsell-amount-description">{{ amountDescription(index) }}</span>
          </span>
        </app-button>

        <app-button variant="outline" :selected="isOneTimeSelected()" class="upsell-option keep-one-time" @click="selectOneTimeOption">
          <span>{{ keepDonationLabel }}</span>
        </app-button>
      </div>
    </div>

    <template #footer>
      <footer-sticky button-text="Continue" @donate-clicked="continueWithSelectedOption" ref="footerSticky" />
      <form-payment-buttons />
    </template>
  </form-layout>
</template>
